import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as RadioGroup from '@radix-ui/react-radio-group'
import { CircleCheck, Leaf, X } from 'lucide-react'
import { toast } from 'sonner'
import { api, ApiError } from '@/lib/api'
import {
  MAX_PACKS_PER_LINE,
  MAX_PO_NUMBER_LENGTH,
  MAX_PURCHASE_REQUEST_LINES,
  type AccountAddress,
  type ProductHit,
  type SubmitPurchaseRequestPayload,
} from '@/lib/api-types'
import { flags } from '@/lib/flags'
import { formatMoney, formatQty } from '@/lib/format'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DataTable, type DataTableColumn } from '@/components/ui/data-table'
import { DescriptionList, type DescriptionItem } from '@/components/ui/description-list'
import { Field, FieldError, FieldHint, MicroLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { PageHeader } from '@/components/ui/page-header'
import { SectionHeader } from '@/components/ui/section-header'
import { Select } from '@/components/ui/select'
import { ProductSearch, StockBadge } from '@/components/catalog/product-search'
import { FileChip } from '@/components/files/file-chip'
import { FileDropZone } from '@/components/files/file-drop-zone'
import { cheaperOption, lineTotal, packOptions, type PackOption } from './pricing'

/**
 * The purchase-order-request wizard: (1) pick products and their pack option
 * with live per-pack pricing, (2) shipping + PO, (3) review & submit. All
 * prices shown here are advisory — the API re-derives pack size, per-kg
 * price, and label from each line's validated priceBreakId on submit. Every
 * price is gated on `flags.purchasingPricing`; off, the wizard shows packs
 * and kg only.
 */

interface WizardLine {
  key: string
  product: ProductHit
  /** Selected pack option — a price-break id from product.tiers. */
  priceBreakId: string
  /** Number of packs, as typed (string state keeps the input friendly). */
  quantity: string
}

const STEPS = ['Products', 'Shipping & PO', 'Review & submit'] as const

let lineSeq = 0

/** Whole packs only: the typed value as an integer 1..10,000 (the API cap), or null. */
function parsePacks(raw: string): number | null {
  if (!/^\d+$/.test(raw)) return null
  const n = Number.parseInt(raw, 10)
  return n >= 1 && n <= MAX_PACKS_PER_LINE ? n : null
}

function selectedPack(line: WizardLine): PackOption | null {
  return packOptions(line.product.tiers).find((o) => o.id === line.priceBreakId) ?? null
}

function lineIsValid(line: WizardLine): boolean {
  return selectedPack(line) !== null && parsePacks(line.quantity) !== null
}

const SUBMIT_ERROR_COPY: Record<string, string> = {
  // invalid_pack is handled separately in the submit onError: the line's pack
  // ids come from its add-time product snapshot, so recovery needs a re-add.
  unknown_ship_to: 'That ship-to address isn’t on your account. Pick one from the list.',
  po_file_required: 'Attach your purchase order document before sending.',
  file_too_large: 'Your PO file is over the 4 MB limit.',
  no_pricebook: 'Pricing isn’t set up for your account yet — contact your Green Line rep.',
  validation_failed: 'Something in the request didn’t validate. Check each step and resend.',
}

// Salesforce's line checks ("Line 2: this pack size is temporarily
// unavailable.") arrive as invalid_request and are written for customers, so
// they are shown as-is. Other invalid_request messages can carry raw
// Salesforce save errors and keep the generic copy.
const LINE_SCOPED_MESSAGE = /^Line \d+: /

export function PurchasingWizardPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [step, setStep] = useState(0)
  const [lines, setLines] = useState<WizardLine[]>([])
  const [shipToCode, setShipToCode] = useState<string | null>(null)
  const [requestedDate, setRequestedDate] = useState('')
  const [poNumber, setPoNumber] = useState('')
  const [poNumberTouched, setPoNumberTouched] = useState(false)
  const [poFile, setPoFile] = useState<File | null>(null)
  const [confirmingLeave, setConfirmingLeave] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  // The line rejected by the API as invalid_pack — its Pack select gets
  // flagged on the Products step until the product is removed.
  const [invalidPackLineKey, setInvalidPackLineKey] = useState<string | null>(null)
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null)
  const showPrices = flags.purchasingPricing

  const accountQuery = useQuery({
    queryKey: ['account'],
    queryFn: ({ signal }) => api.getAccount({ signal }),
    staleTime: 5 * 60_000,
  })
  const account = accountQuery.data
  const shipTos = useMemo(
    () => (account?.addresses ?? []).filter((a) => a.kind === 'shipping'),
    [account],
  )

  // Default ship-to is DERIVED (no effect): until the customer picks one,
  // the account's flagged default — or the only option — is selected.
  const defaultShipTo =
    shipTos.find((a) => a.isDefaultShipTo) ?? (shipTos.length === 1 ? shipTos[0]! : null)
  const effectiveShipToCode = shipToCode ?? defaultShipTo?.code ?? null

  const dirty = lines.length > 0 || poNumber.trim() !== '' || poFile !== null

  // A closed tab loses everything (files can't be persisted) — warn first.
  useEffect(() => {
    if (!dirty || created) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty, created])

  const estimatedTotal = useMemo(
    () =>
      lines.reduce((sum, line) => {
        const pack = selectedPack(line)
        const packs = parsePacks(line.quantity)
        return pack && packs ? sum + lineTotal(pack, packs) : sum
      }, 0),
    [lines],
  )

  const mutation = useMutation({
    mutationFn: ({ payload, file }: { payload: SubmitPurchaseRequestPayload; file: File }) =>
      api.submitPurchaseRequest(payload, file, setUploadProgress),
    onSettled: () => setUploadProgress(null),
    onSuccess: (result) => {
      setCreated(result)
      void queryClient.invalidateQueries({ queryKey: ['purchase-requests'] })
      window.scrollTo({ top: 0 })
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.code === 'invalid_pack') {
        // The line's pack ids come from its add-time product snapshot, so a
        // re-pick can only offer the same dead ids. Recovery = remove the
        // product and add it again; invalidating the search cache makes the
        // re-search fetch fresh tiers instead of serving the stale snapshot.
        void queryClient.invalidateQueries({ queryKey: ['product-search'] })
        const offending = error.lineIndex !== undefined ? lines[error.lineIndex] : undefined
        setInvalidPackLineKey(offending?.key ?? null)
        setSubmitError(
          offending
            ? `The pack options for ${offending.product.name} changed — remove that product and add it again to load the current pack options.`
            : 'A pack option in this request isn’t available anymore — remove that product and add it again to load the current pack options.',
        )
        toast.error('The request didn’t send.')
        return
      }
      if (
        error instanceof ApiError &&
        error.code === 'invalid_request' &&
        LINE_SCOPED_MESSAGE.test(error.message)
      ) {
        setSubmitError(
          `${error.message} Everything you entered is still here — adjust that line and resend.`,
        )
        toast.error('The request didn’t send.')
        return
      }
      const code = error instanceof ApiError ? error.code : 'request_failed'
      setSubmitError(
        SUBMIT_ERROR_COPY[code] ?? 'That didn’t send. Everything you entered is still here — try again.',
      )
      toast.error('The request didn’t send.')
    },
  })

  function addLine(product: ProductHit) {
    if (lines.length >= MAX_PURCHASE_REQUEST_LINES) return
    const first = packOptions(product.tiers)[0] ?? null
    lineSeq += 1
    setLines((current) => [
      ...current,
      {
        key: `line-${lineSeq}`,
        product,
        priceBreakId: first?.id ?? '',
        quantity: '1',
      },
    ])
  }

  /**
   * Another line for a product already on the request — one product ordered
   * in several pack sizes (e.g. 1 × 900 kg IBC + 2 × 25 kg pails). Defaults
   * to the first pack option not already used by that product's lines.
   */
  function addPackLine(product: ProductHit) {
    if (lines.length >= MAX_PURCHASE_REQUEST_LINES) return
    const used = new Set(
      lines.filter((l) => l.product.id === product.id).map((l) => l.priceBreakId),
    )
    const options = packOptions(product.tiers)
    const next = options.find((o) => !used.has(o.id)) ?? options[0] ?? null
    lineSeq += 1
    setLines((current) => [
      ...current,
      {
        key: `line-${lineSeq}`,
        product,
        priceBreakId: next?.id ?? '',
        quantity: '1',
      },
    ])
  }

  function updateLine(key: string, patch: Partial<WizardLine>) {
    setLines((current) => current.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  }

  function removeLine(key: string) {
    setLines((current) => current.filter((l) => l.key !== key))
    // Removing the flagged product IS the invalid_pack recovery.
    setInvalidPackLineKey((current) => (current === key ? null : current))
  }

  function submit() {
    if (!poFile) return
    setSubmitError(null)
    setInvalidPackLineKey(null)
    const payload: SubmitPurchaseRequestPayload = {
      poNumber: poNumber.trim(),
      requestedDate: requestedDate || undefined,
      shipToCode: effectiveShipToCode ?? undefined,
      // Step gating guarantees every line has a pack and whole packs >= 1.
      lines: lines.map((line) => ({
        productId: line.product.id,
        priceBreakId: line.priceBreakId,
        quantity: parsePacks(line.quantity) ?? 1,
      })),
    }
    mutation.mutate({ payload, file: poFile })
  }

  function handleCancel() {
    if (dirty) {
      setConfirmingLeave(true)
      return
    }
    navigate('/purchasing')
  }

  if (created) {
    return <WizardSuccess result={created} />
  }

  const step1Valid = lines.length > 0 && lines.every(lineIsValid)
  const step2Valid = poNumber.trim() !== '' && poFile !== null

  return (
    <div className="mx-auto max-w-form">
      <nav aria-label="Breadcrumb" className="flex items-baseline gap-2 font-mono text-[0.8125rem]">
        <Link
          to="/purchasing"
          className="rounded-sm text-mute underline-offset-4 transition-colors duration-[180ms] ease-editorial hover:text-brand hover:underline"
        >
          Purchasing
        </Link>
        <span aria-hidden="true" className="text-muteSoft">
          ·
        </span>
        <span aria-current="page" className="text-inkMid">
          New order request
        </span>
      </nav>

      <PageHeader
        className="mt-3"
        title="Start a purchase order request"
        description="Pick your products, attach your PO, and send it to Green Line for review."
      />

      <Stepper step={step} />

      {step === 0 ? (
        <ProductsStep
          lines={lines}
          minimumShelfLifeDays={account?.minimumShelfLifeDays ?? null}
          invalidPackLineKey={invalidPackLineKey}
          showPrices={showPrices}
          onAdd={addLine}
          onAddPack={addPackLine}
          onUpdate={updateLine}
          onRemove={removeLine}
        />
      ) : null}

      {step === 1 ? (
        <ShippingStep
          shipTos={shipTos}
          shipToCode={effectiveShipToCode}
          onShipTo={setShipToCode}
          requestedDate={requestedDate}
          onRequestedDate={setRequestedDate}
          poNumber={poNumber}
          poNumberTouched={poNumberTouched}
          onPoNumber={(v) => {
            setPoNumber(v)
            setPoNumberTouched(true)
          }}
          poFile={poFile}
          onPoFile={setPoFile}
        />
      ) : null}

      {step === 2 ? (
        <ReviewStep
          lines={lines}
          showPrices={showPrices}
          estimatedTotal={estimatedTotal}
          shipTo={shipTos.find((a) => a.code === effectiveShipToCode) ?? null}
          requestedDate={requestedDate}
          poNumber={poNumber}
          poFile={poFile}
          submitError={submitError}
          uploadProgress={uploadProgress}
        />
      ) : null}

      {/* Footer controls */}
      <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-rule/50 pt-6">
        {confirmingLeave ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-inkMid">Leave now and this request is gone — it isn&rsquo;t saved.</p>
            <Button type="button" variant="neutral" size="sm" onClick={() => navigate('/purchasing')}>
              Leave anyway
            </Button>
            <Button type="button" variant="quiet" size="sm" onClick={() => setConfirmingLeave(false)}>
              Keep going
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Button type="button" variant="quiet" onClick={handleCancel}>
              Cancel
            </Button>
            {step > 0 ? (
              <Button type="button" variant="neutral" onClick={() => setStep(step - 1)}>
                Back
              </Button>
            ) : null}
          </div>
        )}
        {step < 2 ? (
          <Button
            type="button"
            size="lg"
            disabled={step === 0 ? !step1Valid : !step2Valid}
            onClick={() => setStep(step + 1)}
          >
            {step === 0 ? 'Continue to shipping' : 'Review the request'}
          </Button>
        ) : (
          <Button type="button" size="lg" loading={mutation.isPending} onClick={submit}>
            {mutation.isPending ? 'Sending…' : 'Send to Green Line'}
          </Button>
        )}
      </div>
    </div>
  )
}

/**
 * The wizard stepper, on the manifest rail: the current step wears the rail
 * and a semibold label, finished steps collapse to a filled pine dot, and
 * steps ahead keep their mono number. A transparent rail on the non-current
 * items keeps the row aligned (the sidebar-nav trick).
 */
function Stepper({ step }: { step: number }) {
  return (
    <nav aria-label="Order request steps" className="mt-8">
      <ol className="flex flex-wrap gap-x-6 gap-y-2">
        {STEPS.map((label, index) => {
          const state = index < step ? 'done' : index === step ? 'current' : 'todo'
          return (
            <li
              key={label}
              aria-current={state === 'current' ? 'step' : undefined}
              className={cn(
                'flex items-center gap-2 border-l-2 py-0.5 pl-3',
                state === 'current' ? 'border-brand' : 'border-transparent',
              )}
            >
              {state === 'done' ? (
                <span aria-hidden="true" className="size-1.5 rounded-full bg-brand" />
              ) : (
                <span
                  aria-hidden="true"
                  className={cn(
                    'font-mono text-[0.6875rem]',
                    state === 'current' ? 'text-brandDeep' : 'text-mute',
                  )}
                >
                  {index + 1}
                </span>
              )}
              <span
                className={cn(
                  'text-sm',
                  state === 'current' ? 'font-semibold text-ink' : 'text-mute',
                )}
              >
                {label}
              </span>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

// ---------------------------------------------------------------------------
// Step 1 — Products
// ---------------------------------------------------------------------------

function ProductsStep({
  lines,
  minimumShelfLifeDays,
  invalidPackLineKey,
  showPrices,
  onAdd,
  onAddPack,
  onUpdate,
  onRemove,
}: {
  lines: WizardLine[]
  minimumShelfLifeDays: number | null
  invalidPackLineKey: string | null
  showPrices: boolean
  onAdd: (product: ProductHit) => void
  onAddPack: (product: ProductHit) => void
  onUpdate: (key: string, patch: Partial<WizardLine>) => void
  onRemove: (key: string) => void
}) {
  const addedIds = new Set(lines.map((l) => l.product.id))
  const atLineCap = lines.length >= MAX_PURCHASE_REQUEST_LINES

  // Pack options already used per product — the "Add another pack size"
  // affordance hides once every option of a product is on a line.
  const usedBreaksByProduct = new Map<string, Set<string>>()
  for (const line of lines) {
    const used = usedBreaksByProduct.get(line.product.id) ?? new Set<string>()
    used.add(line.priceBreakId)
    usedBreaksByProduct.set(line.product.id, used)
  }

  return (
    <div className="mt-8 space-y-6">
      {minimumShelfLifeDays ? (
        <p
          role="note"
          className="flex items-start gap-2.5 rounded-lg border border-brand/30 bg-brandTint/50 px-4 py-3 text-sm leading-relaxed text-inkMid"
        >
          <Leaf aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-brand" />
          <span>
            Orders ship meeting your minimum shelf life of{' '}
            <span className="font-semibold text-ink">{minimumShelfLifeDays} days</span>.
          </span>
        </p>
      ) : null}

      <ProductSearch
        id="wizard-search"
        label="Add products"
        addedIds={addedIds}
        atCap={atLineCap}
        capMessage="This request holds the maximum of 50 lines — remove a line to add another."
        showPrices={showPrices}
        onAdd={onAdd}
      />

      {lines.length === 0 ? (
        <p className="border-t border-rule/50 py-10 text-center text-sm text-mute">
          Search the catalog above to add your first line.
        </p>
      ) : (
        <ul className="space-y-4">
          {lines.map((line) => {
            const used = usedBreaksByProduct.get(line.product.id)
            const hasUnusedPack = packOptions(line.product.tiers).some((o) => !used?.has(o.id))
            return (
              <LineCard
                key={line.key}
                line={line}
                invalidPack={line.key === invalidPackLineKey}
                canAddPack={hasUnusedPack}
                atLineCap={atLineCap}
                showPrices={showPrices}
                onAddPack={onAddPack}
                onUpdate={onUpdate}
                onRemove={onRemove}
              />
            )
          })}
        </ul>
      )}
    </div>
  )
}

function LineCard({
  line,
  invalidPack,
  canAddPack,
  atLineCap,
  showPrices,
  onAddPack,
  onUpdate,
  onRemove,
}: {
  line: WizardLine
  /** The API rejected this line's pack option (invalid_pack) — flag the select. */
  invalidPack: boolean
  /** The product still has a pack option not on any line. */
  canAddPack: boolean
  atLineCap: boolean
  /** flags.purchasingPricing — off, the card shows packs and kg only. */
  showPrices: boolean
  onAddPack: (product: ProductHit) => void
  onUpdate: (key: string, patch: Partial<WizardLine>) => void
  onRemove: (key: string) => void
}) {
  const options = packOptions(line.product.tiers)
  const selected = options.find((o) => o.id === line.priceBreakId) ?? null
  const packs = parsePacks(line.quantity)
  const overMaxPacks =
    /^\d+$/.test(line.quantity) && Number.parseInt(line.quantity, 10) > MAX_PACKS_PER_LINE
  const invalidPacks = line.quantity !== '' && packs === null
  const totalKg = selected && packs ? packs * selected.packSizeKg : 0
  const total = selected && packs ? lineTotal(selected, packs) : null
  const cheaper = showPrices && selected ? cheaperOption(line.product.tiers, selected.id) : null

  const idBase = line.key

  return (
    <li>
      <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[0.9375rem] font-semibold text-ink">{line.product.name}</p>
          <p className="mt-0.5 font-mono text-xs text-mute">{line.product.itemCode ?? '—'}</p>
        </div>
        <div className="flex items-center gap-3">
          <StockBadge product={line.product} />
          <button
            type="button"
            onClick={() => onRemove(line.key)}
            aria-label={`Remove ${line.product.name}`}
            className="rounded-sm p-1 text-mute transition-colors duration-[180ms] ease-editorial hover:text-rust"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field>
          <MicroLabel htmlFor={`${idBase}-pack`}>Pack</MicroLabel>
          <Select
            id={`${idBase}-pack`}
            value={line.priceBreakId}
            onChange={(event) => onUpdate(line.key, { priceBreakId: event.target.value })}
            aria-invalid={invalidPack ? true : undefined}
            aria-describedby={invalidPack ? `${idBase}-pack-error` : undefined}
          >
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {showPrices ? `${o.label} · ${formatMoney(o.pricePerKg)}/kg` : o.label}
              </option>
            ))}
          </Select>
          {invalidPack ? (
            <FieldError id={`${idBase}-pack-error`}>
              These pack options are out of date — remove this product and add it again to load
              the current ones.
            </FieldError>
          ) : null}
        </Field>
        <Field>
          <MicroLabel htmlFor={`${idBase}-qty`}>Packs</MicroLabel>
          <Input
            id={`${idBase}-qty`}
            type="number"
            inputMode="numeric"
            min="1"
            max={MAX_PACKS_PER_LINE}
            step="1"
            value={line.quantity}
            onChange={(event) => onUpdate(line.key, { quantity: event.target.value })}
            aria-invalid={invalidPacks ? true : undefined}
            aria-describedby={invalidPacks ? `${idBase}-qty-error` : undefined}
          />
          {invalidPacks ? (
            <FieldError id={`${idBase}-qty-error`}>
              {overMaxPacks
                ? 'Up to 10,000 packs per line.'
                : 'Packs must be a whole number — at least 1.'}
            </FieldError>
          ) : null}
        </Field>
      </div>

      {/* Live line summary — kg, plus price + total when pricing is on */}
      <div className="mt-4 border-t border-rule/40 pt-3.5" aria-live="polite">
        {!selected ? (
          <p className="text-sm text-rust">
            No pack options are set up for this product — contact your Green Line rep.
          </p>
        ) : packs === null ? (
          <p className="text-sm text-mute">
            {showPrices
              ? 'Enter how many packs to see your price.'
              : 'Enter how many packs to see the total kg.'}
          </p>
        ) : showPrices ? (
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
            <p className="text-sm text-inkMid">
              <span className="font-mono font-semibold text-ink">{formatQty(totalKg)} kg</span> at{' '}
              <span className="font-mono font-semibold text-brandDeep">
                {formatMoney(selected.pricePerKg)}/kg
              </span>
            </p>
            <p className="font-mono text-[0.9375rem] font-semibold text-ink">
              {formatMoney(total)}
            </p>
          </div>
        ) : (
          <p className="text-sm text-inkMid">
            <span className="font-mono font-semibold text-ink">{formatQty(totalKg)} kg</span> total
          </p>
        )}
        {selected && cheaper ? (
          <p className="mt-1.5 text-xs text-inkMid">
            Save{' '}
            <span className="font-mono font-semibold text-brandDeep">
              {formatMoney(selected.pricePerKg - cheaper.pricePerKg)}/kg
            </span>{' '}
            with the {cheaper.label}.
          </p>
        ) : null}
        {canAddPack ? (
          <Button
            type="button"
            variant="quiet"
            size="sm"
            className="-ml-3 mt-2"
            disabled={atLineCap}
            onClick={() => onAddPack(line.product)}
          >
            Add another pack size
          </Button>
        ) : null}
      </div>
      </Card>
    </li>
  )
}

// ---------------------------------------------------------------------------
// Step 2 — Shipping & PO
// ---------------------------------------------------------------------------

function ShippingStep({
  shipTos,
  shipToCode,
  onShipTo,
  requestedDate,
  onRequestedDate,
  poNumber,
  poNumberTouched,
  onPoNumber,
  poFile,
  onPoFile,
}: {
  shipTos: AccountAddress[]
  shipToCode: string | null
  onShipTo: (code: string) => void
  requestedDate: string
  onRequestedDate: (value: string) => void
  poNumber: string
  poNumberTouched: boolean
  onPoNumber: (value: string) => void
  poFile: File | null
  onPoFile: (file: File | null) => void
}) {
  const poMissing = poNumberTouched && poNumber.trim() === ''
  return (
    <div className="mt-8 space-y-8">
      {shipTos.length > 0 ? (
        <Field>
          <MicroLabel>Ship to</MicroLabel>
          <RadioGroup.Root
            value={shipToCode ?? ''}
            onValueChange={onShipTo}
            aria-label="Ship to"
            className="grid gap-3 sm:grid-cols-2"
          >
            {shipTos.map((address) => (
              <RadioGroup.Item
                key={address.id}
                value={address.code}
                className={cn(
                  'rounded-lg border bg-card px-4 py-3.5 text-left transition-colors duration-[180ms] ease-editorial',
                  'data-[state=checked]:border-brand data-[state=checked]:bg-brandTint/40',
                  'data-[state=unchecked]:border-rule/80 data-[state=unchecked]:hover:border-mute data-[state=unchecked]:hover:bg-cream',
                )}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-mute">{address.code}</span>
                  {address.isDefaultShipTo ? (
                    <span className="rounded border border-brand/30 bg-brandTint px-1.5 py-0.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-brandDeep">
                      Default
                    </span>
                  ) : null}
                </span>
                <span className="mt-1.5 block text-sm font-medium text-ink">
                  {address.street ?? '—'}
                </span>
                <span className="mt-0.5 block text-xs text-mute">
                  {[address.city, address.state, address.postalCode].filter(Boolean).join(', ')}
                </span>
              </RadioGroup.Item>
            ))}
          </RadioGroup.Root>
        </Field>
      ) : null}

      <div className="grid gap-6 sm:grid-cols-2">
        <Field>
          <MicroLabel htmlFor="wizard-date">Requested date</MicroLabel>
          <Input
            id="wizard-date"
            type="date"
            value={requestedDate}
            onChange={(event) => onRequestedDate(event.target.value)}
            aria-describedby="wizard-date-hint"
          />
          <FieldHint id="wizard-date-hint">When you need the order. Optional.</FieldHint>
        </Field>
        <Field>
          <MicroLabel htmlFor="wizard-po">PO number</MicroLabel>
          <Input
            id="wizard-po"
            value={poNumber}
            onChange={(event) => onPoNumber(event.target.value)}
            placeholder="Your purchase order number"
            maxLength={MAX_PO_NUMBER_LENGTH}
            aria-invalid={poMissing ? true : undefined}
            aria-describedby={poMissing ? 'wizard-po-error' : undefined}
          />
          {poMissing ? (
            <FieldError id="wizard-po-error">Add your PO number — Green Line files the order under it.</FieldError>
          ) : null}
        </Field>
      </div>

      <Field>
        <MicroLabel>Purchase order document</MicroLabel>
        <FileDropZone file={poFile} onChange={onPoFile} />
        <FieldHint>Required — the signed PO that backs this request.</FieldHint>
      </Field>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 3 — Review & submit
// ---------------------------------------------------------------------------

const REVIEW_COLUMNS: DataTableColumn<WizardLine>[] = [
  {
    id: 'product',
    header: 'Product',
    flex: true,
    widthClassName: 'min-w-48',
    cell: (line) => (
      <span className="flex flex-col">
        <span className="truncate text-sm font-medium text-ink" title={line.product.name}>
          {line.product.name}
        </span>
        <span className="mt-0.5 truncate font-mono text-xs text-mute">
          {selectedPack(line)?.label ?? '—'}
        </span>
      </span>
    ),
  },
  {
    id: 'packs',
    header: 'Packs',
    align: 'right',
    widthClassName: 'w-16',
    nowrap: true,
    cell: (line) => formatQty(parsePacks(line.quantity) ?? 0),
  },
  {
    id: 'totalKg',
    header: 'Total kg',
    align: 'right',
    widthClassName: 'w-24',
    nowrap: true,
    cell: (line) => {
      const pack = selectedPack(line)
      const packs = parsePacks(line.quantity)
      return pack && packs ? formatQty(packs * pack.packSizeKg) : '—'
    },
  },
  {
    id: 'pricePerKg',
    header: 'Price / kg',
    align: 'right',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (line) => {
      const pack = selectedPack(line)
      return pack ? formatMoney(pack.pricePerKg) : '—'
    },
  },
  {
    id: 'total',
    header: 'Total',
    align: 'right',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (line) => {
      const pack = selectedPack(line)
      const packs = parsePacks(line.quantity)
      return pack && packs ? formatMoney(lineTotal(pack, packs)) : '—'
    },
  },
]

/** The review columns that carry money — dropped when pricing is off. */
const PRICE_COLUMN_IDS = new Set(['pricePerKg', 'total'])

function ReviewStep({
  lines,
  showPrices,
  estimatedTotal,
  shipTo,
  requestedDate,
  poNumber,
  poFile,
  submitError,
  uploadProgress,
}: {
  lines: WizardLine[]
  /** flags.purchasingPricing — off drops the price columns and the total. */
  showPrices: boolean
  estimatedTotal: number
  shipTo: AccountAddress | null
  requestedDate: string
  poNumber: string
  poFile: File | null
  submitError: string | null
  uploadProgress: number | null
}) {
  const recapFacts: DescriptionItem[] = [
    {
      label: 'Ship to',
      value: shipTo
        ? [shipTo.street, shipTo.city, shipTo.state, shipTo.postalCode].filter(Boolean).join(', ')
        : 'Your default shipping address',
    },
    { label: 'Requested date', value: requestedDate || 'No preference' },
    { label: 'PO number', value: poNumber, mono: true },
    { label: 'PO document', value: poFile ? <FileChip file={poFile} /> : '—' },
  ]
  const columns = showPrices
    ? REVIEW_COLUMNS
    : REVIEW_COLUMNS.filter((column) => !PRICE_COLUMN_IDS.has(column.id))

  return (
    <div className="mt-8 space-y-8">
      <section aria-labelledby="review-lines-heading">
        <SectionHeader id="review-lines-heading" title="Lines" />
        <div className="mt-4">
          <DataTable<WizardLine>
            columns={columns}
            rows={lines}
            rowKey={(line) => line.key}
            caption="Requested lines"
          />
          {showPrices ? (
            <p className="flex justify-end gap-8 border-t border-rule/70 py-3 pr-2">
              <span className="text-sm font-semibold text-ink">Estimated total</span>
              <span className="font-mono text-[0.9375rem] font-semibold tabular-nums text-ink">
                {formatMoney(estimatedTotal)}
              </span>
            </p>
          ) : null}
        </div>
      </section>

      <Card>
        <DescriptionList columns={2} items={recapFacts} />
      </Card>

      <p className="text-sm leading-relaxed text-mute">
        Order requests are reviewed by Green Line before becoming orders.{' '}
        {showPrices
          ? 'Pricing shown is an estimate — your team confirms final pricing on acceptance.'
          : 'Your Green Line team confirms pricing after you send it.'}
      </p>

      {submitError ? (
        <p role="alert" className="rounded-md border border-rust/30 bg-rust/5 px-4 py-3 text-sm text-rust">
          {submitError}
        </p>
      ) : null}

      {uploadProgress !== null ? (
        <div
          role="progressbar"
          aria-label="Upload progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(uploadProgress * 100)}
          className="h-0.5 w-full overflow-hidden rounded-full bg-rule/40"
        >
          <div className="h-full bg-brand" style={{ width: `${Math.round(uploadProgress * 100)}%` }} />
        </div>
      ) : null}
    </div>
  )
}

function WizardSuccess({ result }: { result: { id: string; name: string } }) {
  return (
    <div className="mx-auto max-w-2xl py-10 text-center">
      <CircleCheck aria-hidden="true" className="mx-auto size-8 text-brand" />
      <p className="micro-label mt-6">Order request sent</p>
      <h1 className="mt-2 font-mono text-3xl font-semibold tracking-tight text-ink">{result.name}</h1>
      <p className="mx-auto mt-4 max-w-sm text-[0.9375rem] leading-relaxed text-inkMid">
        It&rsquo;s with your Green Line team for review. Once accepted, it becomes an order and
        shows up on the Orders page — you&rsquo;ll see the decision on the Purchasing page either
        way.
      </p>
      <div className="mt-8 flex justify-center gap-3">
        <Button asChild>
          <Link to={`/purchasing/${result.id}`}>View the order request</Link>
        </Button>
        <Button asChild variant="quiet">
          <Link to="/purchasing">Back to Purchasing</Link>
        </Button>
      </div>
    </div>
  )
}
